///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The time control's view model: everything the rows render, as plain
/// data (percentages, labels, flags) plus a flat set of actions. The
/// React rows are deliberately dumb so the same model can later feed a
/// native overlay through a bridge: serialise everything but `actions`.

import type { EarthBrowserState, TimeZoneMode } from "@/redux/earthBrowserSlice"
import type { TimeSpanMs } from "@/earthbrowser/datasets/TimeLane"

export type ClockState = EarthBrowserState["clockState"]

// Speed presets on a log scale, labeled as simulated time per real second.
// The upper presets are what make cloud/precipitation animation watchable.
export const cSpeedPresets: { value: number; label: string; description: string }[] = [
	{ value: 1, label: "1x", description: "Real-time rate" },
	{ value: 60, label: "1m/s", description: "1 minute of data per second" },
	{ value: 600, label: "10m/s", description: "10 minutes of data per second" },
	{ value: 3600, label: "1h/s", description: "1 hour of data per second" },
	{ value: 21600, label: "6h/s", description: "6 hours of data per second" },
	{ value: 86400, label: "1d/s", description: "1 day of data per second" },
]

export const cMaxOffsetHours = 48

// Label/color for each time-based dataset's availability lane, keyed by the
// engine layer name reported by DatasetLayerManager.getVisibleTimeRanges().
// Unlisted ids are not shown.
export const cLaneMeta: Record<string, { label: string; color: string }> = {
	nexrad: { label: "Radar volumes", color: "#4ade80" },
	radar: { label: "Radar mosaic", color: "#38bdf8" },
	radar_forecast: { label: "Radar forecast", color: "#7dd3fc" },
	mrms: { label: "Precipitation (US)", color: "#67e8f9" },
	imerg: { label: "Precipitation", color: "#22d3ee" },
	clouds: { label: "Clouds", color: "#e5e7eb" },
	cloud_forecast: { label: "Cloud forecast", color: "#cbd5e1" },
	sst: { label: "Sea surface temp", color: "#fb923c" },
	synoptic: { label: "Synoptic", color: "#c084fc" },
	aurora: { label: "Aurora", color: "#f472b6" },
}

/** Format a speed multiplier as simulated time per real second */
export function formatSpeed(speed: number): string {
	if (speed < 60) return `${Math.round(speed)}x`
	if (speed < 3600) return `${Math.round(speed / 60)}m/s`
	if (speed < 86400) return `${Math.round(speed / 3600)}h/s`
	return `${Math.round(speed / 86400)}d/s`
}

/** One strip lane: a visible time-based layer's availability, in strip percentages */
export interface TimeLaneView {
	id: string
	label: string
	color: string
	startPct: number
	endPct: number
	/** Frames loaded on the GPU and displayable right now */
	loaded: { startPct: number; endPct: number }[]
	/** Waiting on the frame for the clock's time (the lane shimmers) */
	waiting: boolean
	// Raw spans, for hit-testing the scrub bubble
	startMs: number
	endMs: number
	loadedRanges: TimeSpanMs[]
}

export interface TimeControlActions {
	togglePlay: () => void
	toggleLive: () => void
	setSpeed: (speed: number) => void
	/** Scrub to a fraction (0-1) of the window; pauses the clock */
	scrubToFraction: (fraction: number) => void
	/** Pause and jump to an instant */
	jumpTo: (ms: number) => void
	stepFrame: (direction: 1 | -1) => void
	/** Grow (1) or shrink (-1) the history / forecast side by one stop */
	adjustHistory: (direction: 1 | -1) => void
	adjustForecast: (direction: 1 | -1) => void
	setHistoryHours: (hours: number) => void
	setForecastHours: (hours: number) => void
	toggleExpanded: () => void
	setTimeZoneMode: (mode: TimeZoneMode) => void
	/** Open the subscribe (or sign-in) flow for a locked history depth */
	openUpsell: () => void
}

export interface TimeControlModel {
	clockState: ClockState
	isLive: boolean
	isAnimating: boolean
	isExpanded: boolean
	timeZoneMode: TimeZoneMode
	/** The window's "now" anchor: wall clock while running, frozen while paused */
	nowMs: number
	currentMs: number
	startMs: number
	endMs: number
	spanMs: number
	nowPct: number
	playheadPct: number
	/** The clock sits outside the window (typed date, arrow keys) */
	beforeWindow: boolean
	afterWindow: boolean
	/** Signed distance of the clock from now */
	offsetFromNowMs: number
	speed: {
		configured: number
		/** After the focused entity's cap (a tracked satellite) */
		effective: number
		capped: boolean
		cap: number | null
	}
	window: {
		historyHours: number
		forecastHours: number
		/** Entitled history depth; past it the stepper opens the upsell */
		maxHistoryHours: number
		maxHours: number
		isPaid: boolean
		/** Primary-layer frames inside the window (what play will step through) */
		frameCount: number
	}
	lanes: TimeLaneView[]
	midnights: { ms: number; pct: number; label: string }[]
	frames: { hasFrames: boolean; prevMs: number | null; nextMs: number | null }
	actions: TimeControlActions
}

/** Strip percentage (clamped 0-100) of an instant within the model's window */
export function stripPct(model: Pick<TimeControlModel, "startMs" | "spanMs">, ms: number): number {
	return Math.max(0, Math.min(100, ((ms - model.startMs) / model.spanMs) * 100))
}
